import { EmbedPlayerAPI, PlayerAPI } from '../EmbedPlayer/Player.types';

/**
 * Creates a proxy for the player API that allows direct calling of internal methods
 * (_setEmbedSetting, etc.) without having to use callMethod explicitly.
 */
export function createPlayerInterface(player: PlayerAPI): EmbedPlayerAPI {
  // Create a wrapper for any method call that might fail
  const safeMethodCall = <T>(
    methodName: string,
    fn: () => T | Promise<T>,
  ): Promise<T | undefined> | T | undefined => {
    try {
      const result = fn();
      if (result instanceof Promise) {
        return result.catch((error) => {
          console.error(`Error calling ${methodName}:`, error);
          return undefined;
        });
      }
      return result;
    } catch (error) {
      console.error(`Error calling ${methodName}:`, error);
      return undefined;
    }
  };

  return new Proxy(player, {
    get(target, prop: string) {
      // If the property exists on the player API, return it directly
      if (prop in target) {
        const value = target[prop as keyof PlayerAPI];

        if (typeof value === 'function') {
          return (...args: unknown[]) => {
            return safeMethodCall(prop, () => {
              return (value as (...args: unknown[]) => unknown).apply(target, args);
            });
          };
        }

        return value;
      }

      // Handle underscore-prefixed methods that need to use callMethod
      if (typeof prop === 'string' && prop.startsWith('_') && target?.callMethod) {
        return (...args: unknown[]) => {
          return safeMethodCall(prop, () => {
            return (target.callMethod as (method: string, ...args: unknown[]) => unknown)(
              prop,
              ...args,
            );
          });
        };
      }

      return undefined;
    },

    set(target: PlayerAPI, prop: string, value: unknown) {
      // For underscore-prefixed properties, use callMethod with 'set' prefix
      if (typeof prop === 'string' && prop.startsWith('_') && target?.callMethod) {
        try {
          // for example _like -> set_like
          const setterName = `set${prop}`;
          (target.callMethod as (method: string, ...args: unknown[]) => unknown)(setterName, value);
          return true;
        } catch (error) {
          console.error(`Error calling ${prop} setter with callMethod:`, error);
          return false;
        }
      }
      return false;
    },
  }) as EmbedPlayerAPI;
}
